var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import cx from "clsx";
import { TYPOGRAPHY_WEIGHTS, TYPOGRAPHY_LEADINGS, TYPOGRAPHY_TRACKINGS } from "./constants";
// FIXME: Make all the typographies component work as first mobile
export var variants = {
    headline: "text-5xl",
    title1: "text-4xl",
    title2: "text-3xl",
    title3: "text-3xl",
    subtitle1: "text-2xl",
    subtitle2: "text-xl",
    body1: "text-base",
    body2: "text-sm",
    body3: "text-lg",
    button: "text-sm",
    caption: "text-xs",
    caption2: "text-xxs",
    overline: "text-xs", // 12px
};
var variantToElement = {
    headline: "h1",
    title1: "h2",
    title2: "h3",
    title3: "h3",
    subtitle1: "h4",
    subtitle2: "h5",
    body1: "p",
    body2: "p",
    body3: "p",
    button: "span",
    caption: "span",
    caption2: "span",
    overline: "span",
};
var variantToLeading = {
    headline: "leading-tight",
    title1: "leading-tight",
    title2: "leading-snug",
    title3: "leading-11",
    subtitle1: "leading-snug",
    subtitle2: "leading-relaxed",
    body1: "leading-normal",
    body2: "leading-normal",
    body3: "leading-relaxed",
    button: "leading-loose",
    caption: "leading-relaxed",
    caption2: "leading-relaxed",
    overline: "leading-7", // 28px
};
var variantToTracking = {
    headline: "tracking-tight",
    title1: "tracking-normal",
    title2: "tracking-normal",
    title3: "tracking-tight",
    body1: "tracking-normal",
    body2: "tracking-normal",
    body3: "tracking-normal",
    subtitle1: "tracking-normal",
    subtitle2: "tracking-normal",
    button: "tracking-wider",
    caption: "tracking-normal",
    caption2: "tracking-normal",
    overline: "tracking-wide", // 0.025em
};
var fontWeight = TYPOGRAPHY_WEIGHTS.reduce(function (acc, curr) {
    var _a;
    return (__assign(__assign({}, acc), (_a = {}, _a[curr] = curr, _a)));
}, {});
var textLeading = TYPOGRAPHY_LEADINGS.reduce(function (acc, curr) {
    var _a;
    return (__assign(__assign({}, acc), (_a = {}, _a[curr] = curr, _a)));
}, {});
var textTracking = TYPOGRAPHY_TRACKINGS.reduce(function (acc, curr) {
    var _a;
    return (__assign(__assign({}, acc), (_a = {}, _a[curr] = curr, _a)));
}, {});
var textTransformationVariants = {
    button: {
        uppercase: true,
    },
    overline: {
        uppercase: true,
    },
};
// Default font weight by variant name
var fontWeightVariants = {
    headline: fontWeight.bold,
    title1: fontWeight.bold,
    title2: fontWeight.bold,
    title3: fontWeight.bold,
    subtitle1: fontWeight.normal,
    subtitle2: fontWeight.semibold,
    body1: fontWeight.normal,
    body2: fontWeight.normal,
    body3: fontWeight.normal,
    button: fontWeight.semibold,
    caption: fontWeight.normal,
    caption2: fontWeight.normal,
    overline: fontWeight.semibold,
};
var getFontWeight = function (weight) {
    if (weight === void 0) { weight = fontWeight.normal; }
    return ({
        "font-normal": weight === fontWeight.normal,
        "font-medium": weight === fontWeight.medium,
        "font-semibold": weight === fontWeight.semibold,
        "font-bold": weight === fontWeight.bold,
    });
};
var getTextLeading = function (leading) {
    switch (leading) {
        case textLeading.none:
            return "leading-none";
        case textLeading.tighter:
            return "leading-tighter";
        case textLeading.tight:
            return "leading-tight";
        case textLeading.tighty:
            return "leading-tighty";
        case textLeading.snug:
            return "leading-snug";
        case textLeading.normal:
            return "leading-normal";
        case textLeading.relaxed:
            return "leading-relaxed";
        case textLeading.loose:
            return "leading-loose";
        case textLeading["3"]:
            return "leading-3";
        case textLeading["4"]:
            return "leading-4";
        case textLeading["5"]:
            return "leading-5";
        case textLeading["6"]:
            return "leading-6";
        case textLeading["7"]:
            return "leading-7";
        case textLeading["8"]:
            return "leading-8";
        case textLeading["9"]:
            return "leading-9";
        case textLeading["10"]:
            return "leading-10";
        case textLeading["11"]:
            return "leading-11";
    }
};
var getTextTracking = function (tracking) {
    switch (tracking) {
        case textTracking.normal:
            return "tracking-normal";
        case textTracking.tighter:
            return "tracking-tighter";
        case textTracking.tight:
            return "tracking-tight";
        case textTracking.wide:
            return "tracking-wide";
        case textTracking.wider:
            return "tracking-wider";
        case textTracking.widest:
            return "tracking-widest";
        case textTracking["1"]:
            return "tracking-1";
        case textTracking["1.25"]:
            return "tracking-1.25";
        case textTracking["2"]:
            return "tracking-2";
        case textTracking["3"]:
            return "tracking-3";
        case textTracking["4"]:
            return "tracking-4";
    }
};
var getFontWeightByVariant = function (variant) {
    return getFontWeight(fontWeightVariants[variant]);
};
var getFontWeightClass = function (weight, variant) {
    return weight ? getFontWeight(weight) : getFontWeightByVariant(variant);
};
var getTextLeadingClass = function (leading, variant) {
    return leading ? getTextLeading(leading) : variantToLeading[variant];
};
var getTextTrackingClass = function (tracking, variant) {
    return tracking
        ? getTextTracking(tracking)
        : variantToTracking[variant]
            ? variantToTracking[variant]
            : undefined;
};
export var Typography = function (_a) {
    var _b;
    var component = _a.component, className = _a.className, _c = _a.variant, variant = _c === void 0 ? "body1" : _c, _d = _a.color, color = _d === void 0 ? "text-gray-300" : _d, children = _a.children, weight = _a.weight, _e = _a.underline, underline = _e === void 0 ? false : _e, _f = _a.italic, italic = _f === void 0 ? false : _f, leading = _a.leading, tracking = _a.tracking, uppercase = _a.uppercase, props = __rest(_a, ["component", "className", "variant", "color", "children", "weight", "underline", "italic", "leading", "tracking", "uppercase"]);
    var fontWeight = getFontWeightClass(weight, variant);
    var textLeading = getTextLeadingClass(leading, variant);
    var textTracking = getTextTrackingClass(tracking, variant);
    var fontStyle = {
        italic: italic,
    };
    var textDecoration = {
        underline: underline,
    };
    var textTransform = {
        uppercase: uppercase !== undefined ? uppercase : (_b = textTransformationVariants[variant]) === null || _b === void 0 ? void 0 : _b.uppercase,
    };
    var cssClasses = cx(variants[variant], color, fontStyle, fontWeight, textLeading, textTracking, textTransform, textDecoration, className);
    var element = component || variantToElement[variant] || "p";
    return React.createElement(element, __assign(__assign({}, props), { className: cssClasses }), children);
};
//# sourceMappingURL=Typography.js.map